import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Character } from '../components/CharacterCard';

export function useCharacters(ideaId: string) {
  const [characters, setCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchCharacters();
  }, [ideaId]);

  const fetchCharacters = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('characters')
        .select(`
          *,
          favorites:character_favorites(count),
          has_favorited:character_favorites(user_id)
        `)
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setCharacters(
        (data || []).map(char => ({
          ...char,
          favorite_count: char.favorites[0]?.count ?? 0,
          has_favorited: char.has_favorited.some(f => f.user_id === user?.id)
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch characters');
    } finally {
      setLoading(false);
    }
  };

  const createCharacter = async (
    name: string,
    role: string,
    description: string,
    avatarUrl?: string,
    personality?: string,
    background?: string,
    goals?: string,
    relationships?: string
  ) => {
    try {
      const { error } = await supabase
        .from('characters')
        .insert({
          idea_id: ideaId,
          name,
          role,
          description,
          avatar_url: avatarUrl,
          personality,
          background,
          goals,
          relationships
        });

      if (error) throw error;
      await fetchCharacters();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create character');
    }
  };

  const updateCharacter = async (
    characterId: string,
    updates: Partial<Omit<Character, 'id' | 'created_at'>>
  ) => {
    try {
      const { error } = await supabase
        .from('characters')
        .update(updates)
        .eq('id', characterId);

      if (error) throw error;
      await fetchCharacters();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update character');
    }
  };

  const deleteCharacter = async (characterId: string) => {
    try {
      const { error } = await supabase
        .from('characters')
        .delete()
        .eq('id', characterId);

      if (error) throw error;
      await fetchCharacters();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to delete character');
    }
  };

  const toggleFavorite = async (characterId: string) => {
    if (!user) return;

    try {
      const { data: existing } = await supabase
        .from('character_favorites')
        .select('id')
        .eq('character_id', characterId)
        .eq('user_id', user.id)
        .single();

      if (existing) {
        await supabase
          .from('character_favorites')
          .delete()
          .eq('character_id', characterId)
          .eq('user_id', user.id);
      } else {
        await supabase
          .from('character_favorites')
          .insert({
            character_id: characterId,
            user_id: user.id
          });
      }

      await fetchCharacters();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to toggle favorite');
    }
  };

  return {
    characters,
    loading,
    error,
    createCharacter,
    updateCharacter,
    deleteCharacter,
    toggleFavorite
  };
}